import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Avatar,
  Button,
  type Column,
  EmptyState,
  Kpi,
  PageHead,
  Pill,
  SearchInput,
  Table,
} from "@/components/common";
import { Guard } from "@/auth/Guard";
import { InviteModal } from "@/components/invitations/InviteModal";
import { useActiveWorkspace } from "@/contexts/WorkspaceContext";
import { useMyWorkspaces } from "@/hooks/useWorkspaces";
import type { Workspace } from "@/types/workspace";
import { formatBRL } from "@/utils/fmt";
import { WorkspaceCreateModal } from "./WorkspaceCreateModal";
import "./WorkspacesListPage.css";

export function WorkspacesListPage() {
  const navigate = useNavigate();
  const { activeWorkspaceId } = useActiveWorkspace();
  const { data: workspaces = [], isLoading } = useMyWorkspaces();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter((w) => w.name.toLowerCase().includes(q) || w.slug.includes(q));
  }, [workspaces, search]);

  const totalMrr = workspaces.reduce((acc, w) => acc + Number(w.plan?.mrr_base ?? 0), 0);
  const activeCount = workspaces.filter((w) => w.active).length;

  const columns: Column<Workspace>[] = [
    {
      id: "name",
      header: "Workspace",
      cell: (w) => (
        <div className="emp-table-name">
          <Avatar name={w.name} size="md" />
          <div>
            <div className="razao">{w.name}</div>
            <div className="fantasia">{w.is_platform_tenant ? "Plataforma TDAX" : w.slug}</div>
          </div>
        </div>
      ),
    },
    { id: "plan", header: "Plano", cell: (w) => w.plan?.name ?? "—" },
    {
      id: "mrr",
      header: "MRR/mês",
      align: "right",
      cell: (w) => formatBRL(w.plan?.mrr_base ?? null),
    },
    {
      id: "status",
      header: "Status",
      cell: (w) => <Pill tone={w.active ? "ativa" : "pausada"}>{w.active ? "Ativo" : "Inativo"}</Pill>,
    },
  ];

  return (
    <>
      <PageHead
        title="Workspaces"
        subtitle="Workspaces que você administra"
        actions={
          <>
            {activeWorkspaceId != null && (
              <Guard permission="users.invitations.create">
                <Button variant="ghost" iconLeft="users" onClick={() => setInviting(true)}>
                  Convidar membros
                </Button>
              </Guard>
            )}
            <Guard permission="workspaces.invitations.create">
              <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
                Criar workspace
              </Button>
            </Guard>
          </>
        }
      />

      <div className="kpi-grid">
        <Kpi icon="building" tone="featured" label="Workspaces" value={String(workspaces.length)} />
        <Kpi icon="shield-check" label="Ativos" value={String(activeCount)} />
        <Kpi icon="credit-card" label="MRR total" value={formatBRL(totalMrr)} />
      </div>

      <div className="ws-list-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar workspace…" />
      </div>

      <Table
        rows={filtered}
        columns={columns}
        getKey={(w) => String(w.id)}
        onRowClick={(w) => navigate(`/workspaces/${w.id}`)}
        empty={
          <EmptyState
            icon="building"
            title={isLoading ? "Carregando…" : "Nenhum workspace"}
            description={isLoading ? undefined : "Crie seu primeiro workspace."}
          />
        }
      />

      {creating && activeWorkspaceId != null && (
        <WorkspaceCreateModal workspaceId={activeWorkspaceId} onClose={() => setCreating(false)} />
      )}
      {inviting && activeWorkspaceId != null && (
        <InviteModal kind="user" workspaceId={activeWorkspaceId} onClose={() => setInviting(false)} />
      )}
    </>
  );
}
